import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getBillByBookingId } from '../utils/billGenerator';
import { generateAndUploadBillPDF } from '../utils/pdfGenerator';
import { getBookingOfferBreakdown } from '../utils/bookingOfferBreakdown';
import { useAuth } from '../contexts/AuthContext';
import { storage, db, ensureAuthTokenReady } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import './BillView.css';

function BillView({ bookingId, onClose }) {
  const { salonData } = useAuth();
  const [bill, setBill] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchBill = useCallback(async () => {
    try {
      setLoading(true);
      const [billData, bookingSnap] = await Promise.all([
        getBillByBookingId(bookingId, salonData.salonId),
        getDoc(doc(db, 'bookings', bookingId))
      ]);

      if (billData) {
        setBill(billData);
      } else {
        setError('Bill not found');
      }
      if (bookingSnap.exists()) {
        setBookingData(bookingSnap.data());
      } else {
        setBookingData(null);
      }
    } catch (err) {
      setError('Failed to load bill');
    } finally {
      setLoading(false);
    }
  }, [bookingId, salonData?.salonId]);

  useEffect(() => {
    if (salonData?.salonId && bookingId) {
      fetchBill();
    }
  }, [bookingId, salonData?.salonId, fetchBill]);

  /** Offer lines: prefer live booking split (salon / Ulloor / other), else stored bill.discounts */
  const displayDiscounts = useMemo(() => {
    if (!bill) return [];
    if (bookingData) {
      const original =
        bookingData.originalPrice ??
        bookingData.servicePrice ??
        bill.subtotal ??
        0;
      const br = getBookingOfferBreakdown({
        ...bookingData,
        originalPrice: original,
        totalPrice: bookingData.totalPrice ?? bill.grandTotal
      });
      const rows = [];
      if (br.salonOfferAmount > 0) {
        rows.push({ description: 'Salon offer', amount: br.salonOfferAmount });
      }
      if (br.ulloorOfferAmount > 0) {
        rows.push({ description: 'Ulloor Styles offer', amount: br.ulloorOfferAmount });
      }
      if (br.otherDiscountAmount > 0) {
        rows.push({ description: 'Other offers', amount: br.otherDiscountAmount });
      }
      if (rows.length > 0) return rows;
    }
    return bill.discounts && bill.discounts.length > 0 ? bill.discounts : [];
  }, [bill, bookingData]);

  /**
   * GST snapshot: prefer live booking data (salon may have GST fields even if the stored
   * bill predates them), fall back to the bill doc. Older bookings/bills have neither field
   * — gstEnabled is falsy/undefined either way, so this safely yields "no GST".
   */
  const gstInfo = useMemo(() => {
    const source = bookingData || bill;
    const enabled = !!source?.gstEnabled;
    if (!enabled) return { enabled: false, percentage: 0, amount: 0 };
    return {
      enabled: true,
      percentage: Number(source.gstPercentage) || 0,
      amount: Number(source.gstAmount) || 0
    };
  }, [bookingData, bill]);

  /**
   * Bill snapshot for PDF / UI totals: grand total must match subtotal minus shown discounts,
   * plus GST on top (stored bill.grandTotal can be stale when discounts are derived from live
   * booking data, or predate the GST feature).
   */
  const billForPdf = useMemo(() => {
    if (!bill) return null;
    const totalDiscount = displayDiscounts.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const sub = Number(bill.subtotal) || 0;
    const preTaxTotal = Math.max(0, sub - totalDiscount);
    const grandTotal = gstInfo.enabled ? preTaxTotal + gstInfo.amount : preTaxTotal;
    return {
      ...bill,
      discounts: displayDiscounts,
      totalDiscount,
      gstEnabled: gstInfo.enabled,
      gstPercentage: gstInfo.percentage,
      gstAmount: gstInfo.amount,
      grandTotal
    };
  }, [bill, displayDiscounts, gstInfo]);

  /** Stored PDF (Firestore URL / Storage) may be outdated if we later derive offers from booking */
  const pdfNeedsRegeneration = useMemo(() => {
    if (!bill || !billForPdf) return false;
    const storedGt = Number(bill.grandTotal) || 0;
    if (Math.abs(billForPdf.grandTotal - storedGt) > 0.01) return true;
    const storedTd = Number(bill.totalDiscount) || 0;
    if (Math.abs((billForPdf.totalDiscount ?? 0) - storedTd) > 0.01) return true;
    if (!!bill.gstEnabled !== !!billForPdf.gstEnabled) return true;
    return false;
  }, [bill, billForPdf]);

  /**
   * True when discount *lines* on the bill doc differ from what we show (booking-driven split).
   * Without this, grandTotal/totalDiscount can match while discounts[] is empty — public /bill page
   * has no offer rows (it cannot read bookings without auth).
   */
  const discountsLineItemsNeedSync = useMemo(() => {
    if (!bill || !billForPdf) return false;
    const norm = (arr) =>
      JSON.stringify(
        (arr || []).map((d) => ({
          description: String(d.description || ''),
          amount: Math.round((Number(d.amount) || 0) * 100) / 100
        }))
      );
    return norm(bill.discounts) !== norm(billForPdf.discounts);
  }, [bill, billForPdf]);

  const billSnapshotNeedsSync = pdfNeedsRegeneration || discountsLineItemsNeedSync;

  /** Keep Firestore + public /bill page in sync with corrected offer totals (no PDF upload). */
  useEffect(() => {
    if (!bill?.id || !billForPdf || !billSnapshotNeedsSync) return;
    let cancelled = false;
    (async () => {
      try {
        await updateDoc(doc(db, 'bills', bill.id), {
          discounts: billForPdf.discounts || [],
          grandTotal: billForPdf.grandTotal,
          totalDiscount: billForPdf.totalDiscount,
          gstEnabled: billForPdf.gstEnabled,
          gstPercentage: billForPdf.gstPercentage,
          gstAmount: billForPdf.gstAmount
        });
        if (!cancelled) {
          setBill((prev) =>
            prev && prev.id === bill.id
              ? {
                  ...prev,
                  discounts: billForPdf.discounts || [],
                  grandTotal: billForPdf.grandTotal,
                  totalDiscount: billForPdf.totalDiscount,
                  gstEnabled: billForPdf.gstEnabled,
                  gstPercentage: billForPdf.gstPercentage,
                  gstAmount: billForPdf.gstAmount
                }
              : prev
          );
        }
      } catch (e) {
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bill?.id, billForPdf, billSnapshotNeedsSync]);

  /**
   * Get or generate PDF URL for the bill
   * First checks if pdfUrl exists in bill document, then checks storage, then generates new PDF
   */
  const getOrGeneratePdfUrl = async () => {
    let downloadURL = bill.pdfUrl;

    // Do not reuse cached PDF if corrected totals/discounts differ (e.g. Ulloor split from booking)
    if (downloadURL && !pdfNeedsRegeneration) {
      return downloadURL;
    }
    if (pdfNeedsRegeneration) {
      console.log('PDF cache skipped: regenerating with corrected offer totals');
    }

    // Try to get PDF from storage (in case it was uploaded but not saved to document)
    if (bill.salonId && bill.billNumber && !pdfNeedsRegeneration) {
      try {
        await ensureAuthTokenReady();
        const fileName = `Bill_${bill.billNumber}.pdf`;
        const storagePath = `bills/${bill.salonId}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        downloadURL = await getDownloadURL(storageRef);

        // Update bill document with PDF URL for future use (non-fatal — we still have the URL)
        if (bill.id) {
          try {
            const billDocRef = doc(db, 'bills', bill.id);
            await updateDoc(billDocRef, {
              pdfUrl: downloadURL,
              pdfFileName: fileName,
              pdfFoundAt: new Date().toISOString()
            });
            console.log('Bill document updated with PDF URL from storage');
          } catch (updateError) {
          }
          setBill((prev) => ({ ...prev, pdfUrl: downloadURL, pdfFileName: fileName }));
        }
        
        return downloadURL;
      } catch (storageError) {
      }
    }
    
    // Generate with corrected lines + grand total (billForPdf)
    console.log('Generating new PDF...');
    const sourceBill = billForPdf || bill;
    const { pdfUrl, pdfFileName } = await generateAndUploadBillPDF(sourceBill);
    downloadURL = pdfUrl;

    // Keep UI + Firestore in sync with corrected totals so we do not loop regeneration
    setBill((prev) => ({
      ...prev,
      pdfUrl,
      pdfFileName,
      grandTotal: sourceBill.grandTotal,
      totalDiscount: sourceBill.totalDiscount,
      discounts: sourceBill.discounts ? [...sourceBill.discounts] : [],
      gstEnabled: sourceBill.gstEnabled,
      gstPercentage: sourceBill.gstPercentage,
      gstAmount: sourceBill.gstAmount
    }));

    if (bill.id) {
      try {
        const billDocRef = doc(db, 'bills', bill.id);
        await updateDoc(billDocRef, {
          pdfUrl,
          pdfFileName,
          pdfGeneratedAt: new Date().toISOString(),
          grandTotal: sourceBill.grandTotal,
          totalDiscount: sourceBill.totalDiscount,
          discounts: sourceBill.discounts || [],
          gstEnabled: sourceBill.gstEnabled,
          gstPercentage: sourceBill.gstPercentage,
          gstAmount: sourceBill.gstAmount
        });
      } catch (persistErr) {
      }
    }

    return downloadURL;
  };

  /**
   * Handle share bill via WhatsApp
   * Uploads PDF to Firebase Storage and shares link via WhatsApp
   */
  const handleSendBill = async () => {
    if (!bill || !bill.customerPhone || bill.customerPhone === 'Not Provided') {
      alert('Customer phone number is not available');
      return;
    }

    let waTab = null;
    try {
      waTab = window.open('about:blank', '_blank');
      if (waTab) {
        try {
          waTab.opener = null;
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* popup blocked before async work — continue; will use location.href fallback */
    }

    try {
      setGenerating(true);

      // Ensure PDF exists (same as download); must complete before opening WhatsApp
      await getOrGeneratePdfUrl();

      // Update share metadata in Firestore
      if (bill.id) {
        try {
          const billDocRef = doc(db, 'bills', bill.id);
          await updateDoc(billDocRef, {
            pdfSharedAt: new Date().toISOString(),
            pdfSharedVia: 'whatsapp'
          });
          console.log('Bill share metadata updated');
        } catch (updateError) {
          // Continue anyway
        }
      }

      // Generate bill page URL dynamically based on Firebase project
      const app = getApp();
      const projectId = app.options.projectId;
      
      // Map project ID to hosting URL (main site)
      const hostingUrlMap = {
        'ulloorsalon': 'https://ulloorsalon-public.web.app',    // Dev main site
        'ulloor-styles': 'https://ulloor-styles.web.app'        // Prod main site
      };
      
      const baseUrl = hostingUrlMap[projectId] || 'https://ulloor-styles.web.app';
      const billPageUrl = (() => {
        try {
          const u = new URL(baseUrl);
          u.pathname = '/bill';
          u.searchParams.set('id', bill.id);
          return u.href;
        } catch {
          return `${String(baseUrl).replace(/\/$/, '')}/bill?id=${encodeURIComponent(bill.id)}`;
        }
      })();
      
      // Format phone number for WhatsApp
      let phoneNumber = bill.customerPhone.replace(/\s+/g, '').replace(/\+/g, '');
      
      // If phone number doesn't start with country code, add 91 (India)
      if (!phoneNumber.startsWith('91') && phoneNumber.length === 10) {
        phoneNumber = '91' + phoneNumber;
      }

      const amountStr = (Number(billForPdf?.grandTotal ?? bill.grandTotal) || 0).toFixed(2);
      const customerLabel = bill.customerName || 'Customer';
      const salonLabel = bill.salonName || 'Salon';

      // Create professional WhatsApp message with bill page link (not direct PDF)
      const message = `Hello ${customerLabel},

Thank you for visiting ${salonLabel}.
We hope you had a great experience.

Bill Details:
• Bill No: ${bill.billNumber ?? '—'}
• Date: ${new Date(bill.billDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
• Amount: Rs. ${amountStr}

View or download your bill:
${billPageUrl}

We look forward to serving you again.

– ${salonLabel}`;

      let encodedText;
      try {
        encodedText = encodeURIComponent(message);
      } catch {
        encodedText = encodeURIComponent(
          message.replace(/[\uD800-\uDFFF]/g, '')
        );
      }

      // Keep URL within common browser limits (~2k for GET)
      const waBase = `https://wa.me/${phoneNumber}?text=`;
      let whatsappUrl = waBase + encodedText;
      if (whatsappUrl.length > 2000) {
        const shortMsg = `${message.split('View or download')[0]}View bill: ${billPageUrl}`;
        whatsappUrl = waBase + encodeURIComponent(shortMsg);
      }

      if (waTab) {
        waTab.location.href = whatsappUrl;
      } else {
        const w = window.open(whatsappUrl, '_blank');
        if (!w) {
          window.location.href = whatsappUrl;
        }
      }

      console.log('WhatsApp opened with bill page link');

    } catch (error) {
      if (waTab) {
        try {
          waTab.close();
        } catch {
          /* ignore */
        }
      }
      if (error.code === 'storage/unauthorized') {
        alert('Permission denied. Please check Firebase Storage rules.');
      } else if (error.code === 'storage/quota-exceeded') {
        alert('Storage quota exceeded. Please contact support.');
      } else {
        alert('Failed to share bill. Please try again or use Download PDF option.');
      }
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Handle download/print bill as PDF
   */
  const handleDownloadPDF = async () => {
    const tab = window.open('about:blank', '_blank');
    if (tab) {
      try {
        tab.opener = null;
      } catch {
        /* ignore */
      }
    }
    try {
      setGenerating(true);

      const downloadURL = await getOrGeneratePdfUrl();

      if (tab) {
        tab.location.href = downloadURL;
      } else {
        const w = window.open(downloadURL, '_blank');
        if (!w) {
          window.location.href = downloadURL;
        }
      }
      console.log('PDF opened successfully');
    } catch (error) {
      if (tab) {
        try {
          tab.close();
        } catch {
          /* ignore */
        }
      }
      const msg =
        error?.code === 'storage/unauthorized'
          ? 'Storage permission denied. Check Firebase rules.'
          : error?.message || 'Failed to generate or open PDF.';
      alert(`Failed to download PDF. ${msg}`);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="bill-view-modal">
        <div className="bill-view-content">
          <div className="loading-container">
            <p>Loading bill...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !bill) {
    return (
      <div className="bill-view-modal">
        <div className="bill-view-content">
          <div className="error-container">
            <p>{error || 'Bill not found'}</p>
            <button onClick={onClose} className="close-btn">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bill-view-modal" onClick={onClose}>
      <div className="bill-view-content" onClick={(e) => e.stopPropagation()}>
        <div className="bill-view-header no-print">
          <h2>Bill Details</h2>
          <button onClick={onClose} className="close-icon-btn">✕</button>
        </div>

        <div className="bill-container">
          {/* Bill Header */}
          <div className="bill-header-section">
            <h1 className="salon-name">{bill.salonName}</h1>
            {bill.salonPhone && (
              <p className="salon-phone">📞 {bill.salonPhone}</p>
            )}
            {bill.salonAddress && (
              <p className="salon-address">{bill.salonAddress}</p>
            )}
          </div>

          <div className="bill-divider"></div>

          {/* Bill Info */}
          <div className="bill-info-section">
            <div className="bill-info-row">
              <span className="info-label">Bill No:</span>
              <span className="info-value bold">{bill.billNumber}</span>
            </div>
            <div className="bill-info-row">
              <span className="info-label">Date & Time:</span>
              <span className="info-value">
                {new Date(bill.billDate).toLocaleString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </div>
            {bill.bookingReference && (
              <div className="bill-info-row">
                <span className="info-label">Booking Ref:</span>
                <span className="info-value">{bill.bookingReference}</span>
              </div>
            )}
          </div>

          <div className="bill-divider"></div>

          {/* Customer Details */}
          <div className="customer-section">
            <h3 className="section-title">Customer Details</h3>
            <div className="customer-info">
              <div className="info-row">
                <span className="info-label">Name:</span>
                <span className="info-value">{bill.customerName}</span>
              </div>
              {bill.customerPhone && bill.customerPhone !== 'Not Provided' && (
                <div className="info-row">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{bill.customerPhone}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bill-divider"></div>

          {/* Services */}
          <div className="services-section">
            <h3 className="section-title">Services</h3>
            <table className="services-table">
              <thead>
                <tr>
                  <th className="col-no">#</th>
                  <th className="col-service">Service</th>
                  <th className="col-price">Price</th>
                </tr>
              </thead>
              <tbody>
                {bill.services.map((service, index) => (
                  <tr key={index}>
                    <td className="col-no">{index + 1}</td>
                    <td className="col-service">{service.name}</td>
                    <td className="col-price">₹{service.price.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bill-divider"></div>

          {/* Bill Summary */}
          <div className="summary-section">
            <div className="summary-row">
              <span className="summary-label">Subtotal:</span>
              <span className="summary-value">₹{bill.subtotal.toFixed(2)}</span>
            </div>

            {displayDiscounts.length > 0 && (
              <>
                {displayDiscounts.map((discount, index) => (
                  <div key={index} className="summary-row discount-row">
                    <span className="summary-label">{discount.description}:</span>
                    <span className="summary-value discount">-₹{Number(discount.amount).toFixed(2)}</span>
                  </div>
                ))}
              </>
            )}

            {gstInfo.enabled && (
              <div className="summary-row gst-row">
                <span className="summary-label">GST ({billForPdf?.gstPercentage ?? gstInfo.percentage}%):</span>
                <span className="summary-value">₹{(billForPdf?.gstAmount ?? gstInfo.amount).toFixed(2)}</span>
              </div>
            )}

            <div className="bill-divider-thin"></div>

            <div className="summary-row grand-total">
              <span className="summary-label">Grand Total:</span>
              <span className="summary-value">₹{(billForPdf?.grandTotal ?? bill.grandTotal).toFixed(2)}</span>
            </div>
          </div>

          <div className="bill-divider"></div>

          {/* Payment Info */}
          <div className="payment-section">
            <div className="payment-row">
              <span className="payment-label">Payment Mode:</span>
              <span className="payment-value">{bill.paymentMode.toUpperCase()}</span>
            </div>
            <div className="payment-row">
              <span className="payment-label">Status:</span>
              <span className="payment-status paid">{bill.billStatus}</span>
            </div>
          </div>

          <div className="bill-divider"></div>

          {/* Footer */}
          <div className="bill-footer">
            <p className="thank-you-message">Thank you for visiting {bill.salonName}!</p>
            <p className="visit-again">We look forward to serving you again! 🙏</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bill-actions no-print">
          <button 
            onClick={handleSendBill} 
            className="action-btn send-whatsapp-btn"
            disabled={!bill.customerPhone || bill.customerPhone === 'Not Provided' || generating}
          >
            {generating ? '⏳ Generating...' : '💬 Share Bill (PDF)'}
          </button>
          <button 
            onClick={handleDownloadPDF} 
            className="action-btn print-btn"
            disabled={generating}
          >
            {generating ? '⏳ Generating...' : '📥 Download PDF'}
          </button>
          <button onClick={onClose} className="action-btn close-btn" disabled={generating}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default BillView;

